import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

export const Env = createEnv({
  server: {
    // ARCJET_KEY: z.string().startsWith('ajkey_').optional(),
    // LOGTAIL_SOURCE_TOKEN: z.string().optional(),
    /** Shared secret for authenticating calls to the `/api/revalidate-path` endpoint. */
    REVALIDATE_SECRET: z.string().min(1).optional(),
    /** Cloudflare zone used by storefront revalidation tag purge. */
    CLOUDFLARE_ZONE_ID: z.string().min(1).optional(),
    /** Cloudflare API token used by storefront revalidation tag purge. */
    CLOUDFLARE_API_TOKEN: z.string().min(1).optional(),
    /** Strapi CMS base URL for server-side marketing content fetches. */
    STRAPI_URL: z.string().url().optional(),
    /** Optional read-only Strapi API token for protected Content API endpoints. */
    STRAPI_API_TOKEN: z.string().min(1).optional(),
    /** Unleash Frontend API URL used by the SSR bootstrap. */
    UNLEASH_SERVER_API_URL: z.string().url().optional(),
    /** Unleash Frontend API token used only by the SSR bootstrap. */
    UNLEASH_SERVER_API_TOKEN: z.string().min(1).optional(),
  },
  client: {
    NEXT_PUBLIC_API_URL: z.string().optional(),

    NEXT_PUBLIC_API: z.string().optional(),
    NEXT_PUBLIC_APP_URL: z.string().optional(),
    NEXT_PUBLIC_ENVIRONMENT: z.enum(['staging', 'production']),
    NEXT_PUBLIC_APP_RELEASE: z.string().min(1).optional(),
    NEXT_PUBLIC_APP_VARIANT: z.enum(['default', 'christmas']).optional(),

    NEXT_PUBLIC_PROD_SOCKET_URL: z.string().optional(),
    NEXT_PUBLIC_STAGE_SOCKET_URL: z.string().optional(),

    NEXT_PUBLIC_FB_PIXEL_ID: z.string().optional(),
    NEXT_PUBLIC_FACEBOOK_APP_ID: z.string().optional(),
    NEXT_PUBLIC_GTM_ID: z.string().optional(),
    NEXT_PUBLIC_TIKTOK_PIXEL_ID: z.string().optional(),
    NEXT_PUBLIC_ANALYTICS_WRITE_KEY: z.string().optional(),
    NEXT_PUBLIC_ANALYTICS_CDP_API_KEY: z.string().optional(),
    NEXT_PUBLIC_ANALYTICS_CDP_SITE_ID: z.string().optional(),
    NEXT_PUBLIC_COOKIEBOT_ID: z.string().optional(),

    NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
    NEXT_PUBLIC_POSTHOG_HOSTNAME: z.string().optional(),
    NEXT_PUBLIC_POSTHOG_RELAY_PATH: z.string().optional(),

    /** Unleash Frontend API URL used by the browser SDK for polling. */
    NEXT_PUBLIC_UNLEASH_FRONTEND_API_URL: z.string().url().optional(),
    /**
     * Public, limited-scope Unleash frontend token used by the browser SDK.
     * Never put a backend/admin token in this NEXT_PUBLIC variable.
     */
    NEXT_PUBLIC_UNLEASH_FRONTEND_API_TOKEN: z.string().min(1).optional(),
    /**
     * Local-only Unleash overrides, applied only under `next dev`.
     * JSON, e.g. `{"GAMIFICATION_ENABLED":true}`.
     */
    NEXT_PUBLIC_LOCAL_FEATURE_FLAG_OVERRIDES: z.string().optional(),

    NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION: z.string().optional(),

    NEXT_PUBLIC_INTERCOM_APP_ID: z.string().optional(),

    NEXT_PUBLIC_WOT_VERIFICATION: z.string().optional(),

    NEXT_PUBLIC_TWITTER_PIXEL_ID: z.string().optional(),
    NEXT_PUBLIC_ONE_SIGNAL_APP_ID: z.string().optional(),

    NEXT_PUBLIC_FINGERPRINTJS_API_KEY_PROD: z.string().optional(),
    NEXT_PUBLIC_FINGERPRINTJS_API_KEY_STAGING: z.string().optional(),

    /** Profile → Security “Install app” row; enable on staging before production rollout. */
    NEXT_PUBLIC_PWA_PROFILE_INSTALL_ENABLED: z.string().optional(),
    /** Shop modal VIP+ request card; enabled per deployment environment. */
    NEXT_PUBLIC_VIP_BUNDLE_REQUEST_CARD_ENABLED: z.string().optional(),
    /** Fair Play routes and navigation entry; enabled per deployment environment. */
    NEXT_PUBLIC_FAIR_PLAY_ENABLED: z.enum(['true', 'false']).optional(),
    /** Local KYC status mock controls; opt in explicitly for development only. */
    NEXT_PUBLIC_KYC_MOCK_SWITCHER_ENABLED: z.enum(['true', 'false']).optional(),
    /** Public Canadian Storefront; only the exact value `true` enables `/ca`. */
    NEXT_PUBLIC_CANADA_STOREFRONT_ENABLED: z.string().optional(),

    NEXT_PUBLIC_CRITEO_GUM_ENDPOINT: z.string().url().optional(),
    NEXT_PUBLIC_CRITEO_PARTNER_ID: z.string().optional(),
    NEXT_PUBLIC_CRITEO_FIRST_PARTY_DOMAIN: z.string().optional(),
    NEXT_PUBLIC_CRITEO_FIRST_PARTY_NAME: z.string().optional(),
  },
  shared: {
    NODE_ENV: z.enum(['test', 'development', 'production']).optional(),
  },
  runtimeEnv: {
    NODE_ENV: process.env.NODE_ENV,

    // Treat empty string as "unset" so optional server env doesn't fail validation in CI/builds.
    REVALIDATE_SECRET: process.env.REVALIDATE_SECRET || undefined,
    CLOUDFLARE_ZONE_ID: process.env.CLOUDFLARE_ZONE_ID || undefined,
    CLOUDFLARE_API_TOKEN: process.env.CLOUDFLARE_API_TOKEN || undefined,
    STRAPI_URL: process.env.STRAPI_URL || undefined,
    STRAPI_API_TOKEN: process.env.STRAPI_API_TOKEN || undefined,
    UNLEASH_SERVER_API_URL: process.env.UNLEASH_SERVER_API_URL || undefined,
    UNLEASH_SERVER_API_TOKEN: process.env.UNLEASH_SERVER_API_TOKEN || undefined,

    NEXT_PUBLIC_TWITTER_PIXEL_ID: process.env.NEXT_PUBLIC_TWITTER_PIXEL_ID,

    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,

    NEXT_PUBLIC_API: process.env.NEXT_PUBLIC_API,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL, // We need for the sitemap building
    NEXT_PUBLIC_ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT, // We need for the sitemap building
    NEXT_PUBLIC_APP_RELEASE: process.env.NEXT_PUBLIC_APP_RELEASE || undefined,
    NEXT_PUBLIC_APP_VARIANT: process.env.NEXT_PUBLIC_APP_VARIANT,

    // Sockets
    NEXT_PUBLIC_PROD_SOCKET_URL: process.env.NEXT_PUBLIC_PROD_SOCKET_URL,
    NEXT_PUBLIC_STAGE_SOCKET_URL: process.env.NEXT_PUBLIC_STAGE_SOCKET_URL,

    // Analytics
    NEXT_PUBLIC_FB_PIXEL_ID: process.env.NEXT_PUBLIC_FB_PIXEL_ID,
    NEXT_PUBLIC_FACEBOOK_APP_ID: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID,
    NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
    NEXT_PUBLIC_TIKTOK_PIXEL_ID: process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID,
    NEXT_PUBLIC_ANALYTICS_WRITE_KEY: process.env.NEXT_PUBLIC_ANALYTICS_WRITE_KEY,
    NEXT_PUBLIC_ANALYTICS_CDP_API_KEY: process.env.NEXT_PUBLIC_ANALYTICS_CDP_API_KEY,
    NEXT_PUBLIC_ANALYTICS_CDP_SITE_ID: process.env.NEXT_PUBLIC_ANALYTICS_CDP_SITE_ID,
    NEXT_PUBLIC_COOKIEBOT_ID: process.env.NEXT_PUBLIC_COOKIEBOT_ID,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOSTNAME: process.env.NEXT_PUBLIC_POSTHOG_HOSTNAME,
    NEXT_PUBLIC_POSTHOG_RELAY_PATH: '/relay-RLaw', // PostHog relay path for proxy routing - must match matcher in proxy.ts
    NEXT_PUBLIC_UNLEASH_FRONTEND_API_URL: process.env.NEXT_PUBLIC_UNLEASH_FRONTEND_API_URL || undefined,
    NEXT_PUBLIC_UNLEASH_FRONTEND_API_TOKEN: process.env.NEXT_PUBLIC_UNLEASH_FRONTEND_API_TOKEN || undefined,
    NEXT_PUBLIC_LOCAL_FEATURE_FLAG_OVERRIDES: process.env.NEXT_PUBLIC_LOCAL_FEATURE_FLAG_OVERRIDES || undefined,

    NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION: process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION,

    NEXT_PUBLIC_INTERCOM_APP_ID: 'w3mu9x0e',

    NEXT_PUBLIC_WOT_VERIFICATION: 'e66cf6e82f576b89f8ba',

    NEXT_PUBLIC_ONE_SIGNAL_APP_ID: process.env.NEXT_PUBLIC_ONE_SIGNAL_APP_ID,

    NEXT_PUBLIC_FINGERPRINTJS_API_KEY_PROD: process.env.NEXT_PUBLIC_FINGERPRINTJS_API_KEY_PROD,
    NEXT_PUBLIC_FINGERPRINTJS_API_KEY_STAGING: process.env.NEXT_PUBLIC_FINGERPRINTJS_API_KEY_STAGING,

    NEXT_PUBLIC_PWA_PROFILE_INSTALL_ENABLED: process.env.NEXT_PUBLIC_PWA_PROFILE_INSTALL_ENABLED,
    NEXT_PUBLIC_VIP_BUNDLE_REQUEST_CARD_ENABLED: process.env.NEXT_PUBLIC_VIP_BUNDLE_REQUEST_CARD_ENABLED,
    NEXT_PUBLIC_FAIR_PLAY_ENABLED: process.env.NEXT_PUBLIC_FAIR_PLAY_ENABLED,
    NEXT_PUBLIC_KYC_MOCK_SWITCHER_ENABLED: process.env.NEXT_PUBLIC_KYC_MOCK_SWITCHER_ENABLED,
    NEXT_PUBLIC_CANADA_STOREFRONT_ENABLED: process.env.NEXT_PUBLIC_CANADA_STOREFRONT_ENABLED,
    // Criteo configuration
    NEXT_PUBLIC_CRITEO_GUM_ENDPOINT: process.env.NEXT_PUBLIC_CRITEO_GUM_ENDPOINT || undefined,
    NEXT_PUBLIC_CRITEO_PARTNER_ID: process.env.NEXT_PUBLIC_CRITEO_PARTNER_ID || undefined,
    NEXT_PUBLIC_CRITEO_FIRST_PARTY_DOMAIN: process.env.NEXT_PUBLIC_CRITEO_FIRST_PARTY_DOMAIN || undefined,
    NEXT_PUBLIC_CRITEO_FIRST_PARTY_NAME: process.env.NEXT_PUBLIC_CRITEO_FIRST_PARTY_NAME || undefined,
  },
});
